/**
 * IBAN utilities — BIC lookup, bank-name lookup, and ISO 13616 checksum
 * validation. Client-side only; used by the affiliate join wizard to give
 * users instant "✓ Geldige NL-IBAN — Rabobank" feedback without a server
 * round-trip.
 *
 * SEPA IBANs encode the bank identifier as characters 5–8 (NL) or 5–7 (BE).
 * Since 2016 EU banks no longer require BIC for domestic SEPA transfers,
 * but many payout systems still ask for it — so we derive it locally to
 * remove one field of typing friction for the affiliate.
 *
 * The tables below cover the ~15 banks that hold >99% of Dutch and Belgian
 * consumer accounts. For less common IBANs (or non-NL/BE SEPA countries)
 * the user can still type BIC manually.
 */

// Format: bank_id (chars 5-8) → [bic, human name]
const NL_BANKS = {
  ABNA: ["ABNANL2A", "ABN AMRO"],
  ASNB: ["ASNBNL21", "ASN Bank"],
  BUNQ: ["BUNQNL2A", "bunq"],
  INGB: ["INGBNL2A", "ING"],
  KNAB: ["KNABNL2H", "Knab"],
  RABO: ["RABONL2U", "Rabobank"],
  RBRB: ["RBRBNL21", "RegioBank"],
  SNSB: ["SNSBNL2A", "SNS Bank"],
  TRIO: ["TRIONL2U", "Triodos Bank"],
  FVLB: ["FVLBNL22", "Van Lanschot"],
  BOFA: ["BOFANLNX", "Bank of America"],
  BNGH: ["BNGHNL2G", "BNG"],
  DEUT: ["DEUTNL2N", "Deutsche Bank"],
  FRNL: ["FRNLNL22", "FR Bank NL"],
  RBOS: ["RBOSNL2A", "Royal Bank of Scotland"],
  NWAB: ["NWABNL2G", "Nederlandse Waterschapsbank"],
  DHBN: ["DHBNNL2R", "DHB Bank"],
  HAND: ["HANDNL2A", "Svenska Handelsbanken"],
  REVOLT: ["REVOLT21", "Revolut"],
};

const BE_BANKS = {
  BBRU: ["BBRUBEBB", "ING België"],
  GKCC: ["GKCCBEBB", "Belfius"],
  GEBA: ["GEBABEBB", "BNP Paribas Fortis"],
  KRED: ["KREDBEBB", "KBC"],
  BPOT: ["BPOTBEB1", "bpost bank"],
  ARSP: ["ARSPBE22", "Argenta"],
  HBKA: ["HBKABE22", "Hello bank"],
  CTBK: ["CTBKBEBX", "Beobank"],
  CREG: ["CREGBEBB", "CBC Banque"],
};

// Country-level IBAN length spec (ISO 13616) — the ones JobGrid users are
// most likely to have. Full list is 70+ countries but we only need SEPA.
const IBAN_LENGTH = {
  NL: 18, BE: 16, DE: 22, FR: 27, ES: 24, IT: 27, PT: 25,
  AT: 20, IE: 22, LU: 20, FI: 18, DK: 18, SE: 24, NO: 15,
  CH: 21, GB: 22, PL: 28, CZ: 24, HU: 28, SK: 24, SI: 19,
  BG: 22, RO: 24, HR: 21, LV: 21, LT: 20, EE: 20, GR: 27,
  MT: 31, CY: 28,
};

function _normalise(iban) {
  return String(iban || "").replace(/[\s-]/g, "").toUpperCase();
}

function _bankTable(iban) {
  const country = iban.slice(0, 2);
  if (country === "NL") return NL_BANKS;
  if (country === "BE") return BE_BANKS;
  return null;
}

/** Return the BIC for a given IBAN or "" if the IBAN's country/bank isn't
 * in our local lookup. Never throws — caller can fall back to manual entry.
 */
export function bicFromIban(iban) {
  const raw = _normalise(iban);
  if (raw.length < 8) return "";
  const table = _bankTable(raw);
  if (!table) return "";
  const key = raw.slice(4, 8);
  return (table[key] || table[key.slice(0, 3)] || [])[0] || "";
}

/** Return the human-friendly bank name for a given IBAN (e.g. "Rabobank")
 * or "" if not recognised. Never throws.
 */
export function bankFromIban(iban) {
  const raw = _normalise(iban);
  if (raw.length < 8) return "";
  const table = _bankTable(raw);
  if (!table) return "";
  const key = raw.slice(4, 8);
  return (table[key] || table[key.slice(0, 3)] || [])[1] || "";
}

/** ISO 13616 mod-97 checksum. Returns true when the check digits match. */
function _mod97(iban) {
  // Move first 4 chars to the back, then convert letters → numbers (A=10, B=11, …)
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let numeric = "";
  for (const ch of rearranged) {
    if (ch >= "0" && ch <= "9") numeric += ch;
    else if (ch >= "A" && ch <= "Z") numeric += (ch.charCodeAt(0) - 55).toString();
    else return false; // stray character
  }
  // Compute mod-97 in chunks to avoid BigInt in old browsers.
  let rem = 0;
  for (const digit of numeric) {
    rem = (rem * 10 + Number(digit)) % 97;
  }
  return rem === 1;
}

/** Validate an IBAN. Returns:
 *   { valid: boolean, country, expectedLen, bank, reason? }
 *
 * A valid IBAN needs: known country, correct length for that country,
 * only alnum characters, and a passing mod-97 checksum.
 */
export function validateIban(iban) {
  const raw = _normalise(iban);
  if (!raw) return { valid: false, reason: "empty" };
  if (raw.length < 5) return { valid: false, reason: "too_short" };
  const country = raw.slice(0, 2);
  const expectedLen = IBAN_LENGTH[country];
  if (!expectedLen) return { valid: false, reason: "unknown_country", country };
  if (raw.length !== expectedLen) {
    return { valid: false, reason: "wrong_length", country, expectedLen, actualLen: raw.length };
  }
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(raw)) {
    return { valid: false, reason: "bad_format", country };
  }
  if (!_mod97(raw)) return { valid: false, reason: "checksum", country };
  return { valid: true, country, expectedLen, bank: bankFromIban(raw) };
}
